import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { BackButton } from '../components/ui/back-button';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { contactAPI } from '../services/api';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const EMPTY_FORM = { name: '', email: '', subject: '', message: '', wants_membership: false, website: '' };

const Contact = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error('Votre message doit contenir au moins 10 caractères');
      return;
    }
    setSending(true);
    try {
      await contactAPI.send(form);
      setSent(true);
      setForm(EMPTY_FORM);
      toast.success('Message envoyé ! Nous vous répondrons rapidement.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Erreur lors de l'envoi du message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 px-4 pb-12" data-testid="contact-page">
      <SEO
        title="Contact"
        description="Contactez l'association de jeu de rôle Just Rôle It : questions, adhésion, organisation de séances."
        path="/contact"
      />
      <div className="container mx-auto max-w-5xl">
        <BackButton />

        <div className="mb-8 md:mb-12">
          <h1 className="text-3xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mb-2 tracking-wide">
            Nous contacter
          </h1>
          <p className="text-gray-400">Une question, envie de rejoindre l'aventure ? Écrivez-nous !</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10">
          {/* Coordonnées */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-6 space-y-5">
              <h2 className="text-lg font-bold text-purple-300">Coordonnées</h2>
              <a href="mailto:association@justroleit.fr" data-testid="contact-email-link" className="flex items-center gap-3 text-gray-300 hover:text-purple-300 transition-colors">
                <Mail className="h-5 w-5 text-purple-400 flex-shrink-0" />
                <span className="text-sm break-all">association@justroleit.fr</span>
              </a>
              <a href="tel:+33689786517" data-testid="contact-phone-link" className="flex items-center gap-3 text-gray-300 hover:text-purple-300 transition-colors">
                <Phone className="h-5 w-5 text-purple-400 flex-shrink-0" />
                <span className="text-sm">06 89 78 65 17</span>
              </a>
              <div className="flex items-center gap-3 text-gray-300">
                <MapPin className="h-5 w-5 text-purple-400 flex-shrink-0" />
                <span className="text-sm">Herblay-sur-Seine, France</span>
              </div>
              <p className="text-xs text-gray-500 pt-2 border-t border-purple-500/20">
                Vos données ne sont utilisées que pour répondre à votre demande.{' '}
                <Link to="/mentions-legales" className="text-purple-400 hover:text-purple-300 underline">
                  En savoir plus
                </Link>
              </p>
            </Card>
          </div>

          {/* Formulaire */}
          <Card className="lg:col-span-3 bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-6 md:p-8">
            {sent ? (
              <div className="text-center py-10 space-y-4" data-testid="contact-success">
                <CheckCircle2 className="h-14 w-14 text-green-400 mx-auto" />
                <h2 className="text-xl font-bold text-purple-300">Merci pour votre message !</h2>
                <p className="text-gray-400">Nous vous répondrons dans les plus brefs délais.</p>
                <Button onClick={() => setSent(false)} variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-900/30" data-testid="contact-send-another">
                  Envoyer un autre message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name" className="text-purple-300">Nom *</Label>
                    <Input id="contact-name" data-testid="contact-name-input" required minLength={2} maxLength={100}
                      value={form.name} onChange={(e) => update('name', e.target.value)}
                      className="bg-black/50 border-purple-500/30 text-white" placeholder="Votre nom" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email" className="text-purple-300">Email *</Label>
                    <Input id="contact-email" data-testid="contact-email-input" type="email" required
                      value={form.email} onChange={(e) => update('email', e.target.value)}
                      className="bg-black/50 border-purple-500/30 text-white" placeholder="vous@exemple.fr" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-subject" className="text-purple-300">Sujet *</Label>
                  <Input id="contact-subject" data-testid="contact-subject-input" required minLength={2} maxLength={150}
                    value={form.subject} onChange={(e) => update('subject', e.target.value)}
                    className="bg-black/50 border-purple-500/30 text-white" placeholder="Demande d'adhésion, question, partenariat..." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-message" className="text-purple-300">Message *</Label>
                  <Textarea id="contact-message" data-testid="contact-message-input" required minLength={10} maxLength={5000} rows={6}
                    value={form.message} onChange={(e) => update('message', e.target.value)}
                    className="bg-black/50 border-purple-500/30 text-white resize-y" placeholder="Votre message..." />
                </div>
                {/* Honeypot anti-spam : caché aux humains */}
                <input type="text" name="website" value={form.website} onChange={(e) => update('website', e.target.value)}
                  tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
                <div className="flex items-center gap-3">
                  <Checkbox id="contact-membership" data-testid="contact-membership-checkbox"
                    checked={form.wants_membership} onCheckedChange={(v) => update('wants_membership', Boolean(v))}
                    className="border-purple-500/50 data-[state=checked]:bg-purple-600" />
                  <Label htmlFor="contact-membership" className="text-gray-300 cursor-pointer">
                    Je souhaite adhérer à l'association
                  </Label>
                </div>
                <Button type="submit" disabled={sending} data-testid="contact-submit-button"
                  className="w-full sm:w-auto px-8 py-5 font-bold bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white neon-button">
                  <Send className="mr-2 h-4 w-4" />
                  {sending ? 'Envoi en cours...' : 'Envoyer le message'}
                </Button>
              </form>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Contact;
